import { useState } from "react";
import { makeApiRequest } from "../../utils";

export function useBrandRatingRubricSections(rubricId) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const validateSection = (data) => {
    // Basic validation - form handles detailed validation
    if (data.rubricScoreWeight != null && isNaN(Number(data.rubricScoreWeight))) {
      throw new Error("Weight must be a valid number");
    }
  };

  const validateQuestion = (data) => {
    if (!data.text?.trim()) {
      throw new Error("Question text is required");
    }
    if (!data.answerChoices || data.answerChoices.length < 2) {
      throw new Error("Questions must have at least 2 answer choices");
    }
    for (const choice of data.answerChoices) {
      if (!choice.text?.trim()) {
        throw new Error("Answer choice text is required");
      }
      if (isNaN(Number(choice.points))) {
        throw new Error("All answer points must be valid numbers");
      }
    }
  };

  const validateSubsection = (data) => {
    // Basic validation - form handles detailed validation
  };

  const createSection = async (data) => {
    validateSection(data);
    setLoading(true);
    setError(null);

    try {
      const response = await makeApiRequest({
        endpoint: `/api/brandRatings/brand_rating_rubric/${rubricId}/sections`,
        method: "POST",
        json: true,
        body: {
          name: data.name,
          rubricScoreWeight: data.rubricScoreWeight ? Number(data.rubricScoreWeight) : null
        }
      });
      setLoading(false);
      return response;
    } catch (err) {
      setError(err);
      setLoading(false);
      throw err;
    }
  };

  const updateSection = async (sectionId, data) => {
    validateSection(data);
    setLoading(true);
    setError(null);

    try {
      const response = await makeApiRequest({
        endpoint: `/api/brandRatings/brand_rating_rubric/${rubricId}/sections/${sectionId}`,
        method: "PATCH",
        json: true,
        body: {
          name: data.name,
          rubricScoreWeight: data.rubricScoreWeight ? Number(data.rubricScoreWeight) : null
        }
      });
      setLoading(false);
      return response;
    } catch (err) {
      setError(err);
      setLoading(false);
      throw err;
    }
  };

  const deleteSection = async (sectionId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await makeApiRequest({
        endpoint: `/api/brandRatings/brand_rating_rubric/${rubricId}/sections/${sectionId}`,
        method: "DELETE"
      });
      setLoading(false);
      return response;
    } catch (err) {
      setError(err);
      setLoading(false);
      throw err;
    }
  };

  const createSubsection = async (sectionId, data) => {
    validateSubsection(data);
    setLoading(true);
    setError(null);

    try {
      const response = await makeApiRequest({
        endpoint: `/api/brandRatings/brand_rating_rubric/${rubricId}/sections/${sectionId}/subsections`,
        method: "POST",
        json: true,
        body: { name: data.name }
      });
      setLoading(false);
      return response;
    } catch (err) {
      setError(err);
      setLoading(false);
      throw err;
    }
  };

  const updateSubsection = async (sectionId, subsectionId, data) => {
    validateSubsection(data);
    setLoading(true);
    setError(null);

    try {
      const response = await makeApiRequest({
        endpoint: `/api/brandRatings/brand_rating_rubric/${rubricId}/sections/${sectionId}/subsections/${subsectionId}`,
        method: "PATCH",
        json: true,
        body: { name: data.name }
      });
      setLoading(false);
      return response;
    } catch (err) {
      setError(err);
      setLoading(false);
      throw err;
    }
  };

  const deleteSubsection = async (sectionId, subsectionId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await makeApiRequest({
        endpoint: `/api/brandRatings/brand_rating_rubric/${rubricId}/sections/${sectionId}/subsections/${subsectionId}`,
        method: "DELETE"
      });
      setLoading(false);
      return response;
    } catch (err) {
      setError(err);
      setLoading(false);
      throw err;
    }
  };

  const createQuestion = async (sectionId, subsectionId, data) => {
    validateQuestion(data);
    setLoading(true);
    setError(null);

    try {
      const response = await makeApiRequest({
        endpoint: `/api/brandRatings/brand_rating_rubric/${rubricId}/sections/${sectionId}/subsections/${subsectionId}/questions`,
        method: "POST",
        json: true,
        body: {
          text: data.text,
          answerChoices: data.answerChoices.map(choice => ({
            text: choice.text,
            points: Number(choice.points)
          }))
        }
      });
      setLoading(false);
      return response;
    } catch (err) {
      setError(err);
      setLoading(false);
      throw err;
    }
  };

  const updateQuestion = async (sectionId, subsectionId, questionId, data) => {
    validateQuestion(data);
    setLoading(true);
    setError(null);

    try {
      const response = await makeApiRequest({
        endpoint: `/api/brandRatings/brand_rating_rubric/${rubricId}/sections/${sectionId}/subsections/${subsectionId}/questions/${questionId}`,
        method: "PATCH",
        json: true,
        body: {
          text: data.text,
          answerChoices: data.answerChoices.map(choice => ({
            id: choice.id,
            text: choice.text,
            points: Number(choice.points)
          }))
        }
      });
      setLoading(false);
      return response;
    } catch (err) {
      setError(err);
      setLoading(false);
      throw err;
    }
  };

  const deleteQuestion = async (sectionId, subsectionId, questionId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await makeApiRequest({
        endpoint: `/api/brandRatings/brand_rating_rubric/${rubricId}/sections/${sectionId}/subsections/${subsectionId}/questions/${questionId}`,
        method: "DELETE"
      });
      setLoading(false);
      return response;
    } catch (err) {
      setError(err);
      setLoading(false);
      throw err;
    }
  };

  const clearError = () => {
    setError(null);
  };

  return {
    createSection,
    updateSection,
    deleteSection,
    createSubsection,
    updateSubsection,
    deleteSubsection,
    createQuestion,
    updateQuestion,
    deleteQuestion,
    clearError,
    loading,
    error
  };
}
